import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  useCreateFaultMutation,
  useDeleteFaultMutation,
} from "../../../api/faultsApi";
import { UploadFilesSection } from "../../../components/FileUploadSection";
import useUploadFaultFile from "../../../hooks/useUploadFaultFile";
import { useListTechniciansQuery } from "../../../api/managementApi";
import useAuth from "../../../hooks/useAuth";
import { useTranslation } from "react-i18next";
import { namespaces } from "../../../consts/i18n";

const FAULT_TYPES = [
  { id: "1", translationKey: "sidebar.panels.faultTypes.hotspot20Less" },
  { id: "2", translationKey: "sidebar.panels.faultTypes.hotspot20-40" },
  { id: "3", translationKey: "sidebar.panels.faultTypes.hotspot40Over" },
  { id: "4", translationKey: "sidebar.panels.faultTypes.bypassDiodeFault1/3" },
  { id: "5", translationKey: "sidebar.panels.faultTypes.bypassDiodeFault2/3" },
  { id: "6", translationKey: "sidebar.panels.faultTypes.bypassDiodeFault3/3" },
  { id: "7", translationKey: "sidebar.panels.faultTypes.optimiserFault" },
  { id: "8", translationKey: "sidebar.panels.faultTypes.stringFault" },
  { id: "9", translationKey: "sidebar.panels.faultTypes.galvinzerFault" },
  { id: "10", translationKey: "sidebar.panels.faultTypes.delaminationFault" },
  { id: "11", translationKey: "sidebar.panels.faultTypes.coloringEVAFault" },
  { id: "12", translationKey: "sidebar.panels.faultTypes.brokenGlassFault" },
  { id: "13", translationKey: "sidebar.panels.faultTypes.generalFault" },
  { id: "14", translationKey: "sidebar.panels.faultTypes.pidFault" },
  { id: "15", translationKey: "sidebar.panels.faultTypes.shadeFault" },
];

export const NewFault = () => {
  const navigate = useNavigate();
  const storeTechnicianName = useSelector((state) => state.technician.name);
  const { projectId, clientId, stateId } = useParams();
  const { t } = useTranslation(namespaces.viewer);

  const [searchParams] = useSearchParams();
  const dimension = searchParams.get("dimension");
  const panelId = searchParams.get("panelId");
  const solarPanelId = searchParams.get("solarPanelId");

  const { data: technicians } = useListTechniciansQuery({ projectId });
  const { isTechnician } = useAuth();

  const [createFault, { isLoading: isCreatingFault }] =
    useCreateFaultMutation();
  const [deleteFault, { isLoading: isDeletingFault }] =
    useDeleteFaultMutation();
  const { uploadFaultFile, isUploading } = useUploadFaultFile();

  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [technicianName, setTechnicianName] = useState(
    storeTechnicianName || "",
  );
  const [faultType, setFaultType] = useState([]);
  const [files, setFiles] = useState([]);
  const [otherFault, setOtherFault] = useState("");
  const [note, setNote] = useState("");

  /**
   * @param {import("react").FormEvent} e
   */
  async function handleSubmit(e) {
    e.preventDefault();

    let faultsList = faultType;
    if (otherFault) {
      faultsList = [...faultsList, otherFault];
    }

    for (const fault of faultsList) {
      try {
        const response = await createFault({
          solarPanelId,
          name: fault,
          note,
          createdAt: new Date(date).toISOString(),
          technicianName,
        }).unwrap();

        const createdFault = response.data;

        if (createdFault && files.length) {
          for (const file of files) {
            // TODO: 🚨 check if right file type & disable button while submitting
            const res = await uploadFaultFile(createdFault.id, [file]);
            if (!res) {
              deleteFault({ faultId: createdFault.id });
              throw new Error("File upload failed");
            }
          }
        }
      } catch (err) {
        console.error(err);
      }
    }

    setFiles([]);

    navigate(
      `/clients/${clientId}/projects/${projectId}/panels/${stateId}/panel-overview?panelId=${panelId}&dimension=${dimension}`,
    );
  }

  return (
    <section className="h-full bg-gray-100">
      <div className="p-8 max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-center">
          {t("sidebar.panels.newFault")}
        </h1>
        <form className="mt-4 text-zinc-500" onSubmit={handleSubmit}>
          <div className="flex gap-8 items-center w-full">
            <label className="flex flex-col gap-1">
              <span className="font-bold text-xs">{t("common.date")}</span>
              <input
                className="mt-1 py-2 px-4 text-sm rounded-lg shadow-sm"
                type="date"
                name="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1 grow">
              <span className="font-bold text-xs">
                {t("common.serviceTechnician")}
              </span>
              {(isTechnician && storeTechnicianName) ||
              (!isTechnician && !technicians?.length) ? (
                <input
                  className="mt-1 py-2 px-4 text-sm rounded-lg shadow-sm"
                  type="text"
                  name="technician"
                  value={technicianName}
                  disabled={isTechnician}
                  onChange={(e) => setTechnicianName(e.target.value)}
                />
              ) : (
                <select
                  name="technician"
                  className="mt-1 py-2 px-4 text-sm rounded-lg shadow-sm"
                  onChange={(e) => setTechnicianName(e.target.value)}
                  defaultValue=""
                >
                  {!technicianName && (
                    <option key="default" disabled value="">
                      {" "}
                      -- {t("common.choose")} --{" "}
                    </option>
                  )}
                  {technicians &&
                    technicians.map((technician) => (
                      <option key={technician.id} value={technician.name}>
                        {technician.name}
                      </option>
                    ))}
                </select>
              )}
            </label>
          </div>
          {/* Fault Types */}
          <div className="mt-8">
            <div className="flex flex-col gap-1">
              <span className="text-primary font-bold text-xs">
                {t("sidebar.panels.faultType")}
              </span>
              <div className="flex flex-wrap gap-4">
                {FAULT_TYPES.map((mappedFaultType) => (
                  <label
                    key={mappedFaultType.id}
                    className="has-[:checked]:bg-primary has-[:checked]:text-white cursor-pointer bg-white flex items-center font-bold text-slate-800 py-2 px-4 text-sm rounded-lg shadow-sm"
                  >
                    <input
                      className="appearance-none"
                      type="checkbox"
                      name="faultType"
                      value={mappedFaultType.id}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFaultType([
                            ...faultType,
                            t(mappedFaultType.translationKey),
                          ]);
                          return;
                        }

                        setFaultType(
                          faultType.filter(
                            (faultTypeId) =>
                              faultTypeId !== t(mappedFaultType.translationKey),
                          ),
                        );
                      }}
                    />
                    <span>{t(mappedFaultType.translationKey)}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* File Input */}
          <UploadFilesSection files={files} onFilesChange={setFiles} />

          {/* Other Faults + Note*/}
          <div className="mt-12 grid grid-cols-2 gap-8">
            <label className="flex flex-col gap-1 grow">
              <span className="text-primary font-bold text-xs">
                {t("sidebar.panels.otherFault")}
              </span>
              <input
                className="mt-1 py-4 px-4 text-sm rounded-lg shadow-sm text-center text-zinc-800 font-bold"
                type="text"
                name="otherFault"
                value={otherFault}
                onChange={(e) => setOtherFault(e.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1 grow">
              <span className="text-primary font-bold text-xs">
                {t("form.note")}
              </span>
              <textarea
                className="mt-1 py-4 px-4 text-sm rounded-lg shadow-sm"
                name="note"
                rows={1}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </label>
          </div>

          {/* Buttons */}
          <div className="mt-16 flex gap-6 w-full justify-center">
            <button
              className="py-4 px-12 bg-white text-black font-bold text-xl rounded-lg shadow-sm"
              onClick={() =>
                navigate(
                  `/clients/${clientId}/projects/${projectId}/panels/${stateId}/panel-overview?panelId=${panelId}`,
                )
              }
            >
              {t("common.throwAway")}
            </button>
            <button
              className="py-4 px-12 bg-primary text-white font-bold text-xl rounded-lg shadow-sm"
              type="submit"
              disabled={isCreatingFault || isDeletingFault || isUploading}
            >
              {t("common.create")}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};
